import { useEffect, useRef, useState } from "react";
import { ChevronDown, Save, Pencil, Trash2, BookmarkCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const BTN = "h-8 px-3 text-xs rounded-md border border-slate-600 hover:bg-slate-800 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40";

export const AiPromptLibrary = ({ prompt, setPrompt, disabled }) => {
  const [items, setItems] = useState([]);
  const [selId, setSelId] = useState("");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const [mode, setMode] = useState(null);
  const [titolo, setTitolo] = useState("");
  const boxRef = useRef(null);
  const sel = items.find((x) => x.id === selId);

  const load = () => api.get("/studio/lettera-ai/prompts").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (it) => { setSelId(it.id); setPrompt(it.testo); setOpen(false); setMode(null); };
  const startSave = () => { if (!prompt.trim()) { toast.error("Scrivi prima l'indicazione da salvare"); return; } setTitolo(""); setMode("save"); };
  const startEdit = () => { if (!sel) return; setTitolo(sel.titolo); setPrompt(sel.testo); setMode("edit"); };
  const confirm = async () => {
    if (!titolo.trim() || !prompt.trim()) { toast.error("Titolo e testo sono obbligatori"); return; }
    try {
      const payload = { titolo: titolo.trim(), testo: prompt.trim() };
      const { data } = mode === "edit" ? await api.put(`/studio/lettera-ai/prompts/${selId}`, payload) : await api.post("/studio/lettera-ai/prompts", payload);
      await load();
      setSelId(data.id);
      setMode(null);
      toast.success(mode === "edit" ? "Indicazione aggiornata" : "Indicazione salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!sel || !window.confirm(`Eliminare l'indicazione «${sel.titolo}»?`)) return;
    try {
      await api.delete(`/studio/lettera-ai/prompts/${sel.id}`);
      setSelId(""); setMode(null); await load();
      toast.success("Indicazione eliminata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2" data-testid="ai-prompt-library">
      <div className="relative" ref={boxRef}>
        <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} data-testid="ai-prompt-select"
          className="w-full h-9 px-3 rounded-md bg-slate-900/60 border border-slate-600 text-sm text-left flex items-center justify-between hover:border-teal-500 transition-colors">
          <span className={sel ? "text-slate-200 truncate" : "text-slate-500"}>{sel ? sel.titolo : items.length ? "Indicazioni salvate..." : "Nessuna indicazione salvata"}</span>
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        </button>
        {open && items.length > 0 && (
          <div className="pgp-cli-dropdown absolute z-50 mt-1 w-full rounded-md shadow-xl" data-testid="ai-prompt-list" onMouseLeave={() => setHover(null)}>
            <div className="max-h-48 overflow-y-auto">
              {items.map((it) => (
                <button type="button" key={it.id} onMouseEnter={() => setHover(it)} onClick={() => pick(it)} data-testid={`ai-prompt-option-${it.id}`}
                  className={`pgp-cli-opt w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${it.id === selId ? "font-semibold" : ""}`}>
                  {it.id === selId ? <BookmarkCheck className="h-3.5 w-3.5 shrink-0" /> : null}<span className="truncate">{it.titolo}</span>
                </button>
              ))}
            </div>
            {hover && (
              <div className="border-t border-slate-700 bg-slate-900/80 px-3 py-2 text-xs text-slate-200 whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="ai-prompt-preview">{hover.testo}</div>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={startSave} disabled={disabled} data-testid="ai-prompt-save-btn" className={BTN}><Save className="h-3.5 w-3.5" />Salva indicazione</button>
        <button type="button" onClick={startEdit} disabled={disabled || !sel} data-testid="ai-prompt-edit-btn" className={BTN}><Pencil className="h-3.5 w-3.5" />Modifica</button>
        <button type="button" onClick={remove} disabled={disabled || !sel} data-testid="ai-prompt-delete-btn" className={`${BTN} hover:text-red-400`}><Trash2 className="h-3.5 w-3.5" />Elimina</button>
      </div>
      {mode && (
        <div className="flex gap-2 items-center rounded-md border border-teal-600/40 bg-teal-500/5 p-2" data-testid="ai-prompt-form">
          <input value={titolo} onChange={(e) => setTitolo(e.target.value)} autoFocus placeholder="Titolo dell'indicazione" data-testid="ai-prompt-title-input"
            className="flex-1 h-8 rounded-md bg-slate-900/60 border border-slate-600 px-2 text-sm text-slate-100 caret-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-500" />
          <button type="button" onClick={confirm} data-testid="ai-prompt-form-confirm" className="h-8 px-3 text-xs font-semibold rounded-md bg-teal-600 text-white hover:bg-teal-500">{mode === "edit" ? "Aggiorna" : "Salva"}</button>
          <button type="button" onClick={() => setMode(null)} data-testid="ai-prompt-form-cancel" className={BTN}>Annulla</button>
        </div>
      )}
      {mode === "edit" && <p className="text-[11px] text-slate-500">Modifica il testo nel campo qui sotto, poi premi Aggiorna.</p>}
    </div>
  );
};
